import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import config from 'config';
import debounce from 'lodash.debounce';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { useUpdateCartDrawerItemQuantityMutation } from '../../services/cartApi';
import { isPreorderable as checkIsPreorderable } from '../../utils/selectors';
import { useToastMessage } from '../CartDrawer/useToastMessage';
import {
  getProductTargetQuantity,
  isSetShoppingBagProduct,
} from '../ShoppingBagBasketItemList/helpers';
import type { ShoppingBagProduct } from '../ShoppingBagBasketItemList/types';

const QuantityWrapper = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.stone};
  height: 32px;
  width: 106px;
  justify-content: space-between;
`;

const QuantityValue = styled.div`
  font-size: 12px;
  margin: 0 12px;
  line-height: 30px;
  text-align: center;
  width: 16px;
  flex-shrink: 0;
`;

const StyledButton = styled.button`
  width: 32px;
  font-size: 24px;
  font-weight: 100;
  color: #18181e;
  margin-top: -4px;
  cursor: pointer;

  &:disabled {
    color: ${props => props.theme.colors.subdued};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  ${({ theme }) => theme.paragraph.variants.paragraph4.textStyles};
  text-transform: none;
  color: ${({ theme }) => theme.colors.error};
  padding-top: ${({ theme }) => theme.spacing.tiny}px;
`;
/**
 * updating is limited by some other value of available products
 * we should set hard limit even when user is mashing plus
 * we should increment instantly, update after the delay, hence,
 * value passed from props should only be used for initial state
 *
 */

const SetQuantityText = styled.span`
  ${props => props.theme.paragraph.variants.paragraph4Uppercase.textStyles};
`;

const setId = config.get('public.productTypes.setId');
const byoSetId = config.get('public.productTypes.byoSetId');

type HandlerReturnType = [(quantity: number) => void, { isLoading: boolean }];

function useUpdateCartDrawerItemQuantityWithParams({
  productId,
  groupKey,
  productTypeId,
}: ShoppingBagProduct): HandlerReturnType {
  const { showToastMessage, hideToastMessage } = useToastMessage();

  const showErrorMessage = useCallback(() => {
    const toastMessage = (
      <FormattedMessage
        id="global_checkout.something_went_wrong"
        defaultMessage="Something went wrong. Please try again."
      />
    );
    hideToastMessage();
    showToastMessage(toastMessage, 6000, true);
  }, [hideToastMessage, showToastMessage]);

  const isSet = productTypeId === setId || productTypeId === byoSetId;
  const productGroupKey = isSet ? groupKey : productId;
  const kind = isSet ? 'productSet' : 'productById';
  const [updateQuantityBase, { isLoading }] =
    useUpdateCartDrawerItemQuantityMutation({
      fixedCacheKey: `quantity-update-${productGroupKey}`,
    });
  const updateQuantity = useCallback(
    quantity => {
      updateQuantityBase({ kind, productGroupKey, quantity }).catch(() =>
        showErrorMessage()
      );
    },
    [kind, productGroupKey, showErrorMessage, updateQuantityBase]
  );
  return [updateQuantity, { isLoading }];
}

function useQuantityErrorMessage({
  availableQuantity,
  quantity,
}: ShoppingBagProduct): React.ReactNode {
  const [error, setError] = useState<React.ReactNode>();
  const hadErrorRef = useRef(false);
  useEffect(() => {
    if (quantity === availableQuantity) {
      setError(
        <FormattedMessage
          id="global_checkout.max_quantity_reached"
          defaultMessage="You’ve reached the maximum quantity available for this item."
        />
      );
      hadErrorRef.current = true;
    } else if (quantity > availableQuantity && availableQuantity > 0) {
      setError(
        <FormattedMessage
          id="global_checkout.max_quantity_exceeded"
          defaultMessage="Only {availableQuantity} left in stock. Please update your quantity to proceed."
          values={{ availableQuantity }}
        />
      );
      hadErrorRef.current = true;
    } else if (hadErrorRef.current) {
      setError(null);
      hadErrorRef.current = false;
    }
  }, [availableQuantity, quantity]);
  return error;
}

function useProductIsOutOfStock(product: ShoppingBagProduct) {
  const isPreorderable = useSelector(state =>
    // @ts-ignore
    checkIsPreorderable(state, { product })
  );

  return useMemo(() => {
    const targetQuantity = getProductTargetQuantity(product, isPreorderable);
    return targetQuantity <= 0;
  }, [isPreorderable, product]);
}

const BasketItemQuantityStepper: FC<{ product: ShoppingBagProduct }> = ({
  product,
}) => {
  const { availableQuantity: maxQuantity, quantity: defaultQuantity } = product;
  const [updateQuantity, { isLoading }] =
    useUpdateCartDrawerItemQuantityWithParams(product);
  const [quantity, setQuantity] = useState<number>(defaultQuantity);

  const isProductOutOfStock = useProductIsOutOfStock(product);
  const [debouncedHandler] = useState(() =>
    debounce(value => {
      updateQuantity(value);
    }, 300)
  );

  const handleQuantityChange = useCallback(
    newValue => {
      const clamped = Math.max(1, Math.min(newValue, maxQuantity));
      setQuantity(newValue);
      debouncedHandler(clamped);
    },
    [debouncedHandler, maxQuantity]
  );

  useEffect(() => {
    return () => {
      debouncedHandler.flush();
    };
  }, [debouncedHandler]);

  const handleIncreaseQuantity = useCallback(() => {
    if (quantity >= maxQuantity || isLoading) {
      return;
    }
    handleQuantityChange(quantity + 1);
  }, [handleQuantityChange, isLoading, maxQuantity, quantity]);
  const handleDecreaseQuantity = useCallback(() => {
    if (quantity < 2 || isLoading) {
      return;
    }
    handleQuantityChange(quantity - 1);
  }, [handleQuantityChange, isLoading, quantity]);

  if (isSetShoppingBagProduct(product)) {
    return (
      <SetQuantityText>
        <FormattedMessage id="global_checkout.qty" defaultMessage="Qty" />:{' '}
        {quantity}
      </SetQuantityText>
    );
  }

  return (
    <>
      <QuantityWrapper>
        <StyledButton
          disabled={isProductOutOfStock || quantity < 2 || isLoading}
          onClick={handleDecreaseQuantity}
          data-autotag="basket-item-quantity-reduce"
          data-qa-automation="BasketItemQuantityReduce"
        >
          -
        </StyledButton>
        <QuantityValue>{quantity}</QuantityValue>
        <StyledButton
          disabled={isProductOutOfStock || quantity >= maxQuantity || isLoading}
          onClick={handleIncreaseQuantity}
          data-autotag="basket-item-quantity-increase"
          data-qa-automation="BasketItemQuantityIncrease"
        >
          +
        </StyledButton>
      </QuantityWrapper>
    </>
  );
};

interface RenderProps {
  errorComponent: JSX.Element;
  stepperComponent: JSX.Element;
}

interface BasketItemQuantityHandlerProps {
  children: (props: RenderProps) => React.ReactNode;
  product: ShoppingBagProduct;
}

type BasketItemQuantityHandlerType = (
  props: PropsWithChildren<BasketItemQuantityHandlerProps>
) => ReactNode;

export const BasketItemQuantityHandler: BasketItemQuantityHandlerType = ({
  children,
  product,
}) => {
  const errorMsg = useQuantityErrorMessage(product);
  const errorComponent = useMemo(
    () => <ErrorMessage>{errorMsg}</ErrorMessage>,
    [errorMsg]
  );
  const stepperComponent = useMemo(
    () => (
      <BasketItemQuantityStepper
        key={`quantity-${product.quantity}`}
        product={product}
      />
    ),
    [product, product.quantity]
  );
  return useMemo(
    () => children({ errorComponent, stepperComponent }),
    [children, errorComponent, stepperComponent]
  );
};
